import { useEffect, useState } from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertFromHTML, EditorState, convertToRaw } from "draft-js";
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import draftToHtml from "draftjs-to-html";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "13.5px",
  outline: "none",
  color: "#0f172a",
  backgroundColor: "#f8fafc",
  transition: "all 0.2s ease",
};

const inputFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
  e.target.style.borderColor = "#294b5a";
  e.target.style.backgroundColor = "#ffffff";
  e.target.style.boxShadow = "0 0 0 3px rgba(41, 75, 90, 0.1)";
};
const inputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
  e.target.style.borderColor = "#cbd5e1";
  e.target.style.backgroundColor = "#f8fafc";
  e.target.style.boxShadow = "none";
};

// ── Skeleton Loader ─────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header Skeleton */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <div style={{
              width: 150,
              height: 28,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{
              width: 60,
              height: 20,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 12,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{
            width: 200,
            height: 18,
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
            backgroundSize: "200% 100%",
            borderRadius: 4,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>
        <div style={{
          width: 140,
          height: 42,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 8,
          animation: "shimmer 1.5s infinite"
        }}></div>
      </div>

      {/* Main Card Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        marginTop: "15px",
        marginBottom: "16px",
      }}>
        {/* Tabs Skeleton */}
        <div style={{ padding: "10px 20px 0", borderBottom: "1px solid #edf2f7", backgroundColor: "#f8fafc", display: "flex", gap: "4px" }}>
          {[1, 2].map((i) => (
            <div key={i} style={{
              padding: "10px 18px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}>
              <div style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: i === 1 ? 80 : 70,
                height: 14,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          ))}
        </div>

        {/* Content Skeleton */}
        <div style={{ padding: "24px 20px" }}>
          <div className="row x-gap-20 y-gap-20">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="col-12">
                <div style={{
                  width: i === 3 ? 60 : 80,
                  height: 14,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 4,
                  marginBottom: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                <div style={{
                  width: "100%",
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
                {i === 2 && (
                  <div style={{
                    width: 180,
                    height: 130,
                    marginTop: 8,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 10,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Skeleton */}
      <div style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
      }}>
        <div style={{
          width: 100,
          height: 42,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 8,
          animation: "shimmer 1.5s infinite"
        }}></div>
        <div style={{
          width: 130,
          height: 42,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 8,
          animation: "shimmer 1.5s infinite"
        }}></div>
      </div>
    </div>
  );
}

const EditBlog = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const { id } = useParams<{ id: string }>();
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Récupération du token
  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const handleAddCategoryClick = () => setShowModal(true);

  const loadCategories = () => {
    axios
      .get(`${API_URL}/category-blog`, config_token)
      .then((response) => setCategories(response.data.data))
      .catch((error) => console.error("Error loading categories:", error));
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleCreateCategory = () => {
    if (!newCategory.trim()) return;

    axios
      .post(`${API_URL}/category-blog-create`, { name: newCategory }, config_token)
      .then(() => {
        setNewCategory("");
        setShowModal(false);
        loadCategories();
        toast.success("Category created successfully");
      })
      .catch((error) => {
        console.error("Error creating category:", error);
        toast.error("Error creating category");
      });
  };

  const handleAddTag = () => {
    if (inputValue.trim() !== "" && !tags.includes(inputValue.trim())) {
      setTags([...tags, inputValue.trim()]);
      setInputValue("");
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  // Load the existing blog to prefill the form.
  useEffect(() => {
    if (!id) return;

    setIsLoading(true);
    axios
      .get(`${API_URL}/blogs/${id}`, config_token)
      .then((response) => {
        const blog = response.data;
        if (!blog) {
          console.error("Blog data is undefined");
          setLoadError(true);
          return;
        }

        setTitle(blog.title || "");
        setCategoryId(blog.category_blog_id?.toString() || "");
        setTags(blog.tags || []);

        if (blog.content) {
          const blocksFromHtml = convertFromHTML(blog.content);
          const state = ContentState.createFromBlockArray(
            blocksFromHtml.contentBlocks,
            blocksFromHtml.entityMap
          );
          setEditorState(EditorState.createWithContent(state));
        }

        if (blog.image) {
          setImageUrl(`${API_URL}/${blog.image}`);
        }
      })
      .catch((error) => {
        console.error("Error loading blog:", error);
        setLoadError(true);
        toast.error("Could not load this blog");
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const handleUpdateBlog = async () => {
    const newErrors: { [key: string]: string } = {};

    if (!title.trim()) newErrors.title = "Title is required";
    if (!categoryId) newErrors.category = "Category is required";

    const contentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    if (!contentHtml || contentHtml === "<p></p>\n") newErrors.content = "Content is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.title || newErrors.category) setTabIndex(0);
      else if (newErrors.content) setTabIndex(1);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", contentHtml);
      formData.append("category_blog_id", categoryId);
      formData.append("tags", JSON.stringify(tags));
      if (selectedFile) formData.append("image", selectedFile);

      // Ajout du token dans les headers
      await axios.post(`${API_URL}/update-blog/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...config_token.headers,
        },
      });

      toast.success("Blog updated successfully");
      navigate("/admin/list-blog");
    } catch (error: any) {
      console.error("Error updating blog:", error.response?.data || error);

      const serverErrors = error?.response?.data?.errors;
      if (serverErrors) {
        const mapped: { [key: string]: string } = {};
        Object.entries(serverErrors).forEach(([field, messages]) => {
          mapped[field === "category_blog_id" ? "category" : field] = Array.isArray(messages)
            ? (messages[0] as string)
            : String(messages);
        });
        setErrors(mapped);
        toast.error(Object.values(mapped)[0] || "Validation failed");
        if (mapped.title || mapped.category) setTabIndex(0);
        else if (mapped.content) setTabIndex(1);
      } else {
        toast.error(error?.response?.data?.message || "Failed to update blog");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1
              style={{
                color: "#294b5a",
                fontSize: "22px",
                fontWeight: 700,
                margin: 0,
                letterSpacing: "-0.02em",
              }}
            >
              Edit Blog
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p style={{ color: "#64748b", fontSize: "13.5px", margin: 0, fontWeight: 400 }}>
            {title ? <>Editing <strong style={{ color: "#294b5a" }}>{title}</strong></> : "Update the details or content below."}
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/admin/list-blog")}
          style={{
            backgroundColor: "#ffffff",
            color: "#294b5a",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            padding: "10px 18px",
            fontSize: "13.5px",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#f8fafc";
            e.currentTarget.style.borderColor = "#94a3b8";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#ffffff";
            e.currentTarget.style.borderColor = "#cbd5e1";
          }}
        >
          <i className="fa-solid fa-arrow-left" style={{ fontSize: "12px" }}></i>
          <span>Back to Blogs</span>
        </button>
      </div>

      {loadError ? (
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "60px 20px",
            textAlign: "center",
            color: "#64748b",
            fontSize: "13.5px",
          }}
        >
          <i className="fa-solid fa-triangle-exclamation mb-2" style={{ fontSize: "24px", color: "#f59e0b", display: "block" }}></i>
          Couldn't load this blog. It may have been deleted, or the link is broken.
          <div style={{ marginTop: "14px" }}>
            <button
              onClick={() => navigate("/admin/list-blog")}
              style={{
                background: "none",
                border: "none",
                color: "#294b5a",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Back to blog list
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* CARTE PRINCIPALE — TABS */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
              overflow: "hidden",
              marginBottom: "16px",
            }}
          >
            <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
              <TabList
                style={{
                  display: "flex",
                  gap: "4px",
                  padding: "10px 20px 0",
                  borderBottom: "1px solid #edf2f7",
                  backgroundColor: "#f8fafc",
                  listStyle: "none",
                  margin: 0,
                }}
              >
                {["Blog Details", "Blog Content"].map((label, i) => (
                  <Tab
                    key={label}
                    style={{
                      padding: "10px 18px",
                      fontSize: "13.5px",
                      fontWeight: 600,
                      cursor: "pointer",
                      borderBottom: tabIndex === i ? "2px solid #294b5a" : "2px solid transparent",
                      color: tabIndex === i ? "#294b5a" : "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "color .15s ease, border-color .15s ease",
                    }}
                  >
                    <span
                      style={{
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        fontSize: "10.5px",
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: tabIndex === i ? "#294b5a" : "#e2e8f0",
                        color: tabIndex === i ? "#d3be76" : "#64748b",
                      }}
                    >
                      {i + 1}
                    </span>
                    {label}
                  </Tab>
                ))}
              </TabList>

              {/* Tab 1: Blog Details */}
              <TabPanel>
                <div style={{ padding: "24px 20px" }}>
                  <div className="row x-gap-20 y-gap-20">
                    <div className="col-12">
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#294b5a", marginBottom: "8px" }}>
                        Title *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter blog title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        style={inputStyle}
                        onFocus={inputFocus}
                        onBlur={inputBlur}
                      />
                      {errors.title && (
                        <p style={{ color: "#dc2626", fontSize: "12.5px", marginTop: "6px" }}>{errors.title}</p>
                      )}
                    </div>

                    <div className="col-12">
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#294b5a", marginBottom: "8px" }}>
                        Category *
                      </label>
                      <div className="d-flex items-center gap-10">
                        <select
                          value={categoryId}
                          onChange={(e) => setCategoryId(e.target.value)}
                          style={{ ...inputStyle, cursor: "pointer", flex: 1 }}
                          onFocus={inputFocus}
                          onBlur={inputBlur}
                        >
                          <option value="">Select a category</option>
                          {categories.map((cat: any) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleAddCategoryClick}
                          style={{
                            backgroundColor: "#294b5a",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "8px",
                            padding: "10px 16px",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "background-color .15s ease",
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1e3743")}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#294b5a")}
                        >
                          <i className="fa-solid fa-plus" style={{ fontSize: "11px" }}></i>
                          Add Category
                        </button>
                      </div>
                      {errors.category && (
                        <p style={{ color: "#dc2626", fontSize: "12.5px", marginTop: "6px" }}>{errors.category}</p>
                      )}
                    </div>

                    {/* Image */}
                    <div className="col-12">
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#294b5a", marginBottom: "8px" }}>
                        Featured Image
                      </label>

                      {imageUrl && (
                        <div style={{ marginBottom: "12px" }}>
                          <img
                            src={imageUrl}
                            alt="Current blog"
                            style={{
                              width: "180px",
                              height: "130px",
                              objectFit: "cover",
                              borderRadius: "10px",
                              border: "1px solid #e2e8f0",
                            }}
                          />
                          <p style={{ fontSize: "12px", color: "#94a3b8", marginTop: "6px" }}>
                            Current image — upload a new file below to replace it.
                          </p>
                        </div>
                      )}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files ? e.target.files[0] : null;
                          setSelectedFile(file);
                          if (file) setImageUrl(URL.createObjectURL(file));
                        }}
                        style={inputStyle}
                      />
                      {selectedFile && (
                        <p style={{ fontSize: "12.5px", color: "#64748b", marginTop: "6px" }}>
                          New file selected: {selectedFile.name}
                        </p>
                      )}
                    </div>

                    {/* Tags */}
                    <div className="col-12">
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#294b5a", marginBottom: "8px" }}>
                        Tags
                      </label>
                      <input
                        type="text"
                        placeholder="Add tags (press Enter or comma to add)"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.key === "Enter" || e.key === ",") && inputValue.trim() !== "") {
                            e.preventDefault();
                            handleAddTag();
                          }
                        }}
                        style={inputStyle}
                        onFocus={inputFocus}
                        onBlur={inputBlur}
                      />

                      {tags.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
                          {tags.map((tag, index) => (
                            <span
                              key={index}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: "#eff6ff",
                                color: "#1e40af",
                              }}
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(index)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  fontSize: "13px",
                                  fontWeight: 700,
                                  color: "#1e40af",
                                  lineHeight: 1,
                                  padding: 0,
                                }}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabPanel>

              {/* Tab 2: Blog Content */}
              <TabPanel>
                <div style={{ padding: "24px 20px" }}>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#294b5a", marginBottom: "8px" }}>
                    Content *
                  </label>
                  <div style={{ border: "1px solid #cbd5e1", borderRadius: "8px", overflow: "hidden", minHeight: "400px" }}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={setEditorState}
                      wrapperClassName="demo-wrapper"
                      editorClassName="demo-editor"
                      placeholder="Write your blog content here..."
                      editorStyle={{ padding: "16px", minHeight: "350px", fontSize: "14px" }}
                    />
                  </div>
                  {errors.content && (
                    <p style={{ color: "#dc2626", fontSize: "12.5px", marginTop: "8px" }}>{errors.content}</p>
                  )}
                </div>
              </TabPanel>
            </Tabs>
          </div>

          {/* ACTIONS FOOTER */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
              padding: "16px 20px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/admin/list-blog")}
              style={{
                padding: "10px 20px",
                fontSize: "13.5px",
                fontWeight: 600,
                color: "#64748b",
                backgroundColor: "#ffffff",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#0f172a";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#64748b";
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpdateBlog}
              disabled={isSaving}
              style={{
                padding: "10px 22px",
                fontSize: "13.5px",
                fontWeight: 600,
                color: "#ffffff",
                backgroundColor: isSaving ? "#5b7684" : "#294b5a",
                border: "none",
                borderRadius: "8px",
                cursor: isSaving ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0 2px 8px rgba(41, 75, 90, 0.25)",
                transition: "all 0.15s ease",
              }}
              onMouseOver={(e) => {
                if (isSaving) return;
                e.currentTarget.style.backgroundColor = "#1e3743";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(41, 75, 90, 0.35)";
              }}
              onMouseOut={(e) => {
                if (isSaving) return;
                e.currentTarget.style.backgroundColor = "#294b5a";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(41, 75, 90, 0.25)";
              }}
            >
              {isSaving ? (
                <>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: "12px" }}></i>
                  Updating...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-check" style={{ fontSize: "12px" }}></i>
                  Update Blog
                </>
              )}
            </button>
          </div>
        </>
      )}

      {/* Modal: Ajouter une catégorie */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
            padding: "16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "440px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 22px",
                borderBottom: "1px solid #edf2f7",
              }}
            >
              <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#294b5a", margin: 0 }}>
                Add New Blog Category
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  lineHeight: 1,
                  cursor: "pointer",
                  fontSize: "1.4rem",
                  color: "#64748b",
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "22px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#294b5a", marginBottom: "8px" }}>
                Category Name
              </label>
              <input
                type="text"
                placeholder="Enter category name"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                style={inputStyle}
                onFocus={inputFocus}
                onBlur={inputBlur}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                padding: "16px 22px",
                borderTop: "1px solid #edf2f7",
              }}
            >
              <button
                type="button"
                onClick={() => setShowModal(false)}
                style={{
                  padding: "10px 18px",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#64748b",
                  backgroundColor: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.color = "#0f172a";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateCategory}
                style={{
                  padding: "10px 18px",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#ffffff",
                  backgroundColor: "#294b5a",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(41, 75, 90, 0.25)",
                  transition: "all 0.15s ease",
                }}
                onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#1e3743")}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "#294b5a")}
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditBlog;